/**
 * Hybrid Radar Composable
 * Manages radar overlays using weather.com as primary source,
 * with NWS WMS (CONUS) and RainViewer (international) as fallbacks.
 * NWS tiles are recolored with a rain-only color palette.
 */

import { ref, shallowRef, computed, watch } from "vue";
import { useWeatherStore } from "@/stores/weather.store";
import type { MapPreferences } from "@/stores/settings.store";
import {
  isWithinCONUS,
  buildRainViewerTileUrl,
  buildWeathercomTileUrl,
} from "@/utils/radarProvider";
import { RadarTileCache } from "@/utils/RadarTileCache";
import { RecoloredNWSMapType } from "@/utils/RecoloredNWSMapType";
import { initRecolorWorker } from "@/utils/recolorRadarTile";
import type { RadarSource, RadarTimestamp } from "@/utils/radarProvider";

/** Unified overlay wrapper so we can call setOpacity on both types */
interface RadarOverlay extends google.maps.MapType {
  setOpacity(opacity: number): void;
  dispose?(): void;
}

export function useHybridRadar(
  preferences: () => Pick<
    MapPreferences,
    "showRadarLayer" | "radarOpacity" | "radarAnimationSpeed"
  >,
  sourceOverride?: () => RadarSource | undefined,
) {
  const weatherStore = useWeatherStore();

  const activeSource = ref<RadarSource>("weathercom");
  const currentFrameIndex = ref(0);
  const isPlaying = ref(false);
  const isTilesLoading = ref(false);

  /** Track how many overlays are currently loading tiles */
  let overlaysLoadingCount = 0;

  let animationInterval: ReturnType<typeof setInterval> | null = null;
  let inventoryTimer: ReturnType<typeof setTimeout> | null = null;
  let mapRef: google.maps.Map | null = null;
  let sourceLocation: { lat: number; lng: number } | null = null;
  const radarOverlays: RadarOverlay[] = [];
  const frameKeys: string[] = [];
  // Keep frame objects for selection/playback, but mount only the visible one.
  let mountedOverlay: RadarOverlay | null = null;
  // Published with the reconciled overlays, so controls never index stale frames.
  const timestamps = shallowRef<RadarTimestamp[]>([]);
  const tileCache = new RadarTileCache();
  let layersReady = false;
  let generation = 0;
  let suspended = false;
  let resumePlayback = false;

  /** Combined timestamps for the active source */
  const availableTimestamps = computed<RadarTimestamp[]>(() => {
    if (activeSource.value === "weathercom") {
      return weatherStore.weathercomRadarTimestamps;
    }
    if (activeSource.value === "nws") {
      return weatherStore.nwsRadarTimestamps;
    }
    return weatherStore.radarTimestamps.map((frame) => ({
      source: "rainviewer" as const,
      epoch: frame.epoch,
      tileUrlBase: frame.tileUrlBase,
    }));
  });

  const timeDisplay = computed(() => {
    if (timestamps.value.length === 0) return "--:--";
    const ts = timestamps.value[currentFrameIndex.value];
    if (!ts) return "--:--";
    const date = new Date(ts.epoch * 1000);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  });

  /** Choose the requested source, then the available provider for this location. */
  function determineSource(lat: number, lng: number): RadarSource {
    const override = sourceOverride?.();
    if (override) return override;
    if (weatherStore.weathercomRadarTimestamps.length > 0) {
      return "weathercom";
    }
    // Use NWS inside CONUS only when its capabilities supplied real frames.
    if (isWithinCONUS(lat, lng) && weatherStore.nwsRadarTimestamps.length > 0) {
      return "nws";
    }
    return "rainviewer";
  }

  /** RainViewer serves tiles through zoom 7, so reveal its overlay when selected. */
  function fitSourceZoom(source: RadarSource): void {
    if (source === "rainviewer" && (mapRef?.getZoom?.() ?? 0) > 7) {
      mapRef?.setZoom(7);
    }
  }

  async function fetchTimestamps(): Promise<void> {
    await weatherStore.fetchAllRadarTimestamps();
  }

  /** Check the next radar inventory slot while a map is mounted. */
  function scheduleInventoryRefresh(): void {
    if (inventoryTimer) clearTimeout(inventoryTimer);
    if (suspended || !mapRef || typeof document === "undefined") return;
    const now = Date.now();
    const interval = 5 * 60_000;
    const nextCheck = (Math.floor((now - 3 * 60_000) / interval) + 1) * interval
      + 3 * 60_000;
    inventoryTimer = setTimeout(async () => {
      if (!document.hidden) await weatherStore.fetchWeathercomRadarTimestampsAction();
      scheduleInventoryRefresh();
    }, nextCheck - now);
  }

  /** Resume the radar inventory when a hidden map becomes visible. */
  function onMapVisible(): void {
    if (typeof document === "undefined" || document.hidden || suspended || !mapRef) return;
    void weatherStore.fetchWeathercomRadarTimestampsAction();
    scheduleInventoryRefresh();
  }

  /** Attach the selected provider's frames to the existing map. */
  async function initLayers(
    map: google.maps.Map,
    lat: number,
    lng: number,
  ): Promise<void> {
    const run = ++generation;
    mapRef = map;
    sourceLocation = { lat, lng };

    if (run !== generation) return;
    const source = determineSource(lat, lng);
    if (source === "nws") await initRecolorWorker();
    if (run !== generation) return;
    activeSource.value = source;
    fitSourceZoom(source);

    reconcileOverlays();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onMapVisible);
      document.addEventListener("visibilitychange", onMapVisible);
    }
    scheduleInventoryRefresh();
  }

  /** Swap providers only when the selected location or override requires it. */
  function switchSource(lat: number, lng: number): void {
    sourceLocation = { lat, lng };
    const newSource = determineSource(lat, lng);
    fitSourceZoom(newSource);
    if (newSource === activeSource.value) return;

    activeSource.value = newSource;
    reconcileOverlays();
  }

  /** Reconcile frame identities without mounting tiles for hidden frames. */
  function reconcileOverlays(): void {
    if (!mapRef) return;
    if (activeSource.value === "nws") void initRecolorWorker();

    const ts = availableTimestamps.value;
    const keys = ts.map(frameKey);
    if (
      keys.length === frameKeys.length &&
      keys.every((key, i) => key === frameKeys[i])
    )
      return;

    const selectedKey = frameKeys[currentFrameIndex.value];
    const followingLatest =
      !isPlaying.value && currentFrameIndex.value === frameKeys.length - 1;
    const retained = new Set(keys);
    // Remove in reverse order so surviving frame identities stay stable.
    for (let i = frameKeys.length - 1; i >= 0; i--) {
      if (retained.has(frameKeys[i])) continue;
      if (mountedOverlay === radarOverlays[i]) {
        mapRef.overlayMapTypes.removeAt(0);
        mountedOverlay = null;
      }
      radarOverlays[i].dispose?.();
      radarOverlays.splice(i, 1);
      frameKeys.splice(i, 1);
    }

    for (let i = 0; i < ts.length; i++) {
      const frame = ts[i];
      const existingIndex = frameKeys.indexOf(keys[i]);
      if (existingIndex >= 0) {
        // Frame order may change without requiring new tiles.
        if (existingIndex !== i) {
          const [overlay] = radarOverlays.splice(existingIndex, 1);
          frameKeys.splice(existingIndex, 1);
          radarOverlays.splice(i, 0, overlay);
          frameKeys.splice(i, 0, keys[i]);
        }
        continue;
      }
      let created: RadarOverlay;
      if (frame.source === "weathercom") {
        // Weather.com tiles are pre-colored - use standard ImageMapType
        const overlay = new google.maps.ImageMapType({
          getTileUrl: (coord, zoom) => {
            return buildWeathercomTileUrl(coord, zoom, frame.epoch);
          },
          tileSize: new google.maps.Size(256, 256),
          maxZoom: 12,
          opacity: 0,
          name: `Radar-${frame.epoch}`,
        });
        created = overlay;
      } else if (frame.source === "nws" && frame.isoString) {
        // Use canvas-based recolored overlay for NWS (rain-only colors)
        const overlay = new RecoloredNWSMapType(
          frame.isoString,
          `Radar-${frame.epoch}`,
          tileCache,
        );
        overlay.setOpacity(0);

        // Track tile loading state across all overlays
        overlay.setOnTileLoadingChange((loading) => {
          if (loading) {
            overlaysLoadingCount++;
          } else {
            overlaysLoadingCount = Math.max(0, overlaysLoadingCount - 1);
          }
          isTilesLoading.value = overlaysLoadingCount > 0;
        });

        created = overlay;
      } else {
        // Use standard ImageMapType for RainViewer
        const overlay = new google.maps.ImageMapType({
          getTileUrl: (coord, zoom) => {
            return buildRainViewerTileUrl(coord, zoom, frame.tileUrlBase!);
          },
          tileSize: new google.maps.Size(256, 256),
          maxZoom: 7,
          opacity: 0,
          name: `Radar-${frame.epoch}`,
        });
        created = overlay;
      }
      radarOverlays.splice(i, 0, created);
      frameKeys.splice(i, 0, keys[i]);
    }

    timestamps.value = [...ts];
    layersReady = ts.length > 0;
    const retainedSelection = selectedKey ? keys.indexOf(selectedKey) : -1;
    currentFrameIndex.value =
      followingLatest || retainedSelection < 0
        ? Math.max(0, ts.length - 1)
        : retainedSelection;
    if (!layersReady) stopPlayback();
    updateFrame();
  }

  function frameKey(frame: RadarTimestamp): string {
    return `${frame.source}:${frame.epoch}:${frame.isoString ?? frame.tileUrlBase ?? ""}`;
  }

  /** Mount only the visible radar frame and apply the caller's opacity. */
  function updateFrame(): void {
    if (!mapRef) return;
    const visible = layersReady && preferences().showRadarLayer;
    const nextOverlay = visible ? radarOverlays[currentFrameIndex.value] : null;
    if (mountedOverlay !== nextOverlay) {
      if (mountedOverlay) {
        mapRef.overlayMapTypes.removeAt(0);
        if (mountedOverlay instanceof RecoloredNWSMapType) {
          mountedOverlay.releaseAllTiles();
        }
        mountedOverlay = null;
      }
      if (nextOverlay) {
        mapRef.overlayMapTypes.insertAt(0, nextOverlay);
        mountedOverlay = nextOverlay;
      }
    }
    mountedOverlay?.setOpacity(preferences().radarOpacity);
  }

  function togglePlayback(): void {
    if (isPlaying.value) {
      stopPlayback();
    } else {
      startPlayback();
    }
  }

  function startPlayback(): void {
    stopPlayback();
    if (timestamps.value.length === 0) return;
    isPlaying.value = true;
    animationInterval = setInterval(() => {
      nextFrame();
    }, preferences().radarAnimationSpeed);
  }

  function stopPlayback(): void {
    isPlaying.value = false;
    if (animationInterval) {
      clearInterval(animationInterval);
      animationInterval = null;
    }
  }

  function nextFrame(): void {
    if (timestamps.value.length === 0) return;
    currentFrameIndex.value =
      (currentFrameIndex.value + 1) % timestamps.value.length;
    updateFrame();
  }

  function prevFrame(): void {
    if (timestamps.value.length === 0) return;
    currentFrameIndex.value =
      (currentFrameIndex.value - 1 + timestamps.value.length) %
      timestamps.value.length;
    updateFrame();
  }

  function goToLatest(): void {
    if (timestamps.value.length === 0) return;
    stopPlayback();
    currentFrameIndex.value = timestamps.value.length - 1;
    updateFrame();
  }

  // React to opacity and visibility setting changes in real-time
  watch(
    [() => preferences().radarOpacity, () => preferences().showRadarLayer],
    () => {
      if (!suspended) updateFrame();
    },
  );

  // A local source choice replaces radar overlays without fetching weather or rebuilding the map.
  watch(
    () => sourceOverride?.(),
    () => {
      if (!suspended && mapRef && sourceLocation) {
        switchSource(sourceLocation.lat, sourceLocation.lng);
      }
    },
  );

  // Refresh radar when weather data is refreshed (auto-refresh or manual)
  watch(
    () => weatherStore.lastUpdated,
    async () => {
      if (!mapRef || suspended) return;
      const run = generation;
      await fetchTimestamps();
      if (run === generation && !suspended) reconcileOverlays();
    },
  );

  /** Pause hidden route work while retaining overlays and decoded tiles. */
  function suspend(): void {
    if (suspended) return;
    suspended = true;
    generation++;
    if (inventoryTimer) clearTimeout(inventoryTimer);
    inventoryTimer = null;
    resumePlayback = isPlaying.value;
    stopPlayback();
  }

  /** Refresh metadata and continue playback when a cached route returns. */
  async function resume(): Promise<void> {
    if (!suspended) return;
    suspended = false;
    const run = generation;
    await fetchTimestamps();
    if (run !== generation || suspended) return;
    if (mapRef && sourceLocation) {
      switchSource(sourceLocation.lat, sourceLocation.lng);
      reconcileOverlays();
      updateFrame();
    }
    scheduleInventoryRefresh();
    if (resumePlayback) startPlayback();
    resumePlayback = false;
  }

  // A mounted map polls published radar frames independently of weather updates.
  watch(
    () => weatherStore.weathercomRadarTimestamps,
    () => {
      if (suspended || !mapRef || !sourceLocation) return;
      const source = determineSource(sourceLocation.lat, sourceLocation.lng);
      if (source !== activeSource.value) {
        switchSource(sourceLocation.lat, sourceLocation.lng);
      } else if (source === "weathercom") {
        reconcileOverlays();
      }
    },
  );

  /** Release timers, listeners, and retained frame resources when the map is disposed. */
  function cleanup(): void {
    generation++;
    suspended = false;
    resumePlayback = false;

    if (inventoryTimer) clearTimeout(inventoryTimer);
    inventoryTimer = null;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onMapVisible);
    }
    stopPlayback();
    for (const overlay of radarOverlays) overlay.dispose?.();
    tileCache.clear();
    if (mapRef) {
      mapRef.overlayMapTypes.clear();
    }
    mountedOverlay = null;
    radarOverlays.length = 0;
    frameKeys.length = 0;
    timestamps.value = [];
    overlaysLoadingCount = 0;
    isTilesLoading.value = false;
    layersReady = false;
    mapRef = null;
    sourceLocation = null;
  }

  function getMemoryUsage() {
    return radarOverlays.reduce(
      (total, overlay) => {
        if (overlay instanceof RecoloredNWSMapType) {
          const memory = overlay.getMemoryUsage();
          for (const key of Object.keys(total) as (keyof typeof total)[])
            total[key] += memory[key];
        }
        return total;
      },
      {
        cachedTiles: 0,
        cachedBytes: 0,
        canvases: 0,
        canvasBytes: 0,
        pending: 0,
      },
    );
  }

  return {
    getMemoryUsage,
    timestamps,
    currentFrameIndex,
    isPlaying,
    activeSource,
    timeDisplay,
    isTilesLoading,
    fetchTimestamps,
    initLayers,
    switchSource,
    reconcileOverlays,
    togglePlayback,
    nextFrame,
    prevFrame,
    goToLatest,
    stopPlayback,
    suspend,
    resume,
    cleanup,
  };
}
